import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { LabelService } from '../../services/label.service';
import { LabelAndDescription } from '../../models/patient-model';
import {
  documentsCategories,
  documentsCategoriesForNewModule,
} from '../../models/hardcoded-data';
import moment from 'moment';
import { MatIcon } from '@angular/material/icon';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-panel-with-document-upload',
  templateUrl: './panel-with-document-upload.component.html',
  styleUrls: ['./panel-with-document-upload.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
  standalone: false,
})
export class PanelWithDocumentUploadComponent implements OnInit, OnChanges {
  @Input() title: string = '';
  @Input() inputBoxes: LabelAndDescription[] = [];
  @Input() isEditing: boolean = false;
  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<any>();
  @Output() confirmForEdit = new EventEmitter<any>();

  documentForm: FormGroup;
  fileLoaded: boolean = false;
  showMessageError = false;
  showErrorFileSize = false;
  fileData: File | null = null;
  isBtnClicked: boolean = false;
  documentsCategories: string[] = [];

  constructor(
    public labelService: LabelService,
    private fb: FormBuilder,
    private dateAdapter: DateAdapter<any>
  ) {
    this.documentForm = this.fb.group({});
    this.dateAdapter.setLocale('it-IT');
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputBoxes'] && this.inputBoxes.length > 0) {
      this.initForm();
      if (this.inputBoxes.find((input) => input.label === 'category')) {
        this.documentsCategories = documentsCategoriesForNewModule;
      }
    }
  }

  initForm(): void {
    const formConfig: { [key: string]: any } = {};
    this.inputBoxes.forEach((input) => {
      if (input.label === 'date') {
        formConfig[input.label] = [
          moment(input.description, 'DD/MM/YYYY'),
          Validators.required,
        ];
      } else {
        formConfig[input.label] = [
          input.description || '',
          Validators.required,
        ];
      }
    });
    this.documentForm = this.fb.group(formConfig);
  }

  selectCategory(category: string): void {
    this.documentForm.get('category')?.setValue(category);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  removeFile(): void {
    this.fileData = null;
    this.fileLoaded = false;
    this.showMessageError = false;
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    let files: FileList | null = element.files;
    if (files && files.length > 0) {
      const file = files[0];
      const maxSize = 32 * 1024 * 1024; // 32MB
      if (file.size > maxSize) {
        this.showMessageError = true;
        this.showErrorFileSize = true;
        return;
      }

      this.fileLoaded = true;
      this.fileData = file;
      this.showMessageError = false;
      this.showErrorFileSize = false;
    }
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onConfirm(): void {
    const formValues = this.documentForm.value;
    switch (this.isEditing) {
      case true:
        if (this.documentForm.invalid) {
          this.showMessageError = true;
          return;
        }
        const outputEdit = {
          inputs: this.inputBoxes.map((input) => ({
            ...input,
            description:
              input.label === 'date'
                ? moment(formValues[input.label]).format('DD/MM/YYYY')
                : formValues[input.label],
          })),
        };
        this.isBtnClicked = true;
        this.confirmForEdit.emit(outputEdit);
        break;
      case false:
        if (this.isBtnClicked || this.documentForm.invalid || !this.fileData) {
          this.showMessageError = true;
          return;
        }
        const output = {
          file: this.fileData,
          inputs: this.inputBoxes.map((input) => ({
            ...input,
            description:
              input.label === 'date'
                ? moment(formValues[input.label]).format('DD/MM/YYYY')
                : formValues[input.label],
          })),
        };
        this.isBtnClicked = true;
        this.confirm.emit(output);
        break;
    }
  }
}
